/**
 * What a submission and a delivery have in common.
 *
 * Extracted from ProjectDetailPage on 2026-08-25. These four — the storage
 * cell, the two hooks and the removal dialog — were used by both the
 * Submissions and the Results tab, which is why they live here rather than in
 * either: a copy in each is two answers to "is this record's data still there",
 * and that question has been got wrong enough times already.
 */
import { useState } from "react";
import { api } from "../api";
import { Badge, Btn, ConfirmDialog, Spinner } from "../components/Shared";
import { useDisplayTimezone } from "../portalConfig";
import { formatWhen } from "../components/Shared";

export function fmt(b) {
  if (!b) return "0 B";
  if (b < 1024)       return b + " B";
  if (b < 1024 ** 2)  return (b / 1024).toFixed(1) + " KB";
  if (b < 1024 ** 3)  return (b / 1024 ** 2).toFixed(1) + " MB";
  return (b / 1024 ** 3).toFixed(2) + " GB";
}

export const FILE_ACCESSORS = {
  name: f => typeof f === "string" ? f.split("/").pop() : f.name,
  size: f => typeof f === "object" && f.size ? f.size : 0,
  md5:  f => typeof f === "object" && f.md5 ? f.md5 : "",
};

// A submission or delivery is only a record of where files were written — after
// a storage migration the row looks exactly as healthy as it ever did. This is
// the one place that says otherwise, so it has to distinguish "the files are
// gone" from "we could not look", which are very different pieces of news.
// Why an archived project is archived. NULL is a real state — everything
// archived before this was recorded, and anything closed through the plain
// status dropdown — and it deliberately shows nothing rather than guessing.
export const ARCHIVE_LOOK = {
  finished:  { label: "Finished",  bg: "#ecfdf5", fg: "#065f46" },
  cancelled: { label: "Cancelled", bg: "#fef2f2", fg: "#991b1b" },
  expired:   { label: "Expired",   bg: "#fffbeb", fg: "#92400e" },
};

export const VERIFY_LOOK = {
  ok:           { color: "green", label: "Verified" },
  missing:      { color: "red",   label: "Files missing" },
  partial:      { color: "red",   label: "Partly missing" },
  unverifiable: { color: "amber", label: "Can't check" },
};

// s3_health_ok === false covers two very different situations, and this banner
// used to say the same thing about both — "not reachable" was printed over a
// backend message stating the bucket answered fine, which is exactly the
// contradiction that made a missing-data incident unreadable. Keys are the
// backend's S3_HEALTH_KINDS; tests/test_health_kind_contract.py fails if the
// backend gains a kind that is missing here.
export const HEALTH_LOOK = {
  unreachable: {
    headline: "This project's storage is not reachable.",
    body: "Uploads and result packaging will fail until it is fixed.",
    fix: " Use “Check now” in S3 Upload Destination after fixing the settings.",
  },
  empty_prefix: {
    headline: "This project's storage folder is empty.",
    body: "The bucket answers, but none of the data this project has on record is in it — it was "
        + "most likely deleted, or moved without the portal being told. New uploads will still "
        + "work; a delivery packaged from this folder would come out empty.",
    fix: " Check the Storage column on the Submissions and Results tabs to see which records point "
       + "at data that is gone. A record whose data is no longer there can be removed.",
  },
};

export function StorageCell({ row, canManage, busy, onVerify }) {
  const zone = useDisplayTimezone();
  const look = VERIFY_LOOK[row.verified_state];
  const when = row.verified_at ? formatWhen(row.verified_at, { zone }) : null;
  const title = [row.verified_detail, when && `Checked ${when}`].filter(Boolean).join("\n");

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span title={title || undefined}>
        {look
          ? <Badge color={look.color}>{look.label}</Badge>
          : <span style={{ fontSize: 12, color: "#6b7280" }}>Not checked</span>}
      </span>
      {canManage && (
        <Btn variant="ghost" disabled={busy} onClick={() => onVerify(row)}
             style={{ fontSize: 11, padding: "3px 8px" }}>
          {busy ? <Spinner size={10} /> : look ? "↻" : "Check"}
        </Btn>
      )}
    </div>
  );
}

// Shared by both tabs: POST the verify endpoint, then reload so the persisted
// state and its explanation come back from the server rather than being
// guessed at here.
export function useVerifier(url, reload, onError) {
  const [busy, setBusy] = useState({});
  const verify = async row => {
    setBusy(b => ({ ...b, [row.id]: true }));
    try {
      await api.post(url(row));
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(b => ({ ...b, [row.id]: false }));
      reload();
    }
  };
  return [busy, verify];
}

// Removing a record, shared by both tabs because the two differ only in the two
// URLs. The preview is a POST: it re-checks storage and persists the answer, so
// what the dialog shows and what the DELETE will allow cannot disagree.
export function useRemover(base, reload, onError, onProjectChanged) {
  const [info, setInfo]   = useState(null);   // { row, ...preview } | { row, loading: true }
  const [busy, setBusy]   = useState(false);

  const open = async row => {
    setInfo({ row, loading: true });
    try {
      setInfo({ row, ...await api.post(`${base(row)}/deletion-preview`) });
    } catch (err) {
      setInfo(null);
      onError(err.message);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.delete(base(info.row));
      setInfo(null);
      reload();
      // The server re-ran the health check as part of the delete, so this only
      // has to pull the answer down — that is what clears the banner and the
      // tab counts without a page reload.
      onProjectChanged?.();
    } catch (err) {
      setInfo(null);
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return { info, busy, open, confirm, close: () => setInfo(null) };
}

export function RemoveRecordDialog({ info, busy, onConfirm, onClose }) {
  const look = VERIFY_LOOK[info.verified_state];
  const kind = info.kind === "delivery" ? "delivery" : "submission";
  return (
    <ConfirmDialog
      title={info.loading ? "Remove this record" : `Remove the record of ${info.label}`}
      confirmWord={info.confirm_word || ""}
      confirmLabel="Remove record"
      // Disabled while the preview is in flight: until it lands `blockers` is
      // empty, so the dialog would otherwise offer an action it is about to
      // refuse.
      busy={busy || !!info.loading}
      blockers={info.blockers || []}
      onConfirm={onConfirm}
      onClose={onClose}
    >
      {info.loading ? (
        <div style={{ fontSize: 13, color: "#6b7280" }}>
          <Spinner /> Checking storage before offering to remove this…
        </div>
      ) : (
        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
          <p style={{ margin: "0 0 10px" }}>
            This removes the portal's record of {kind} <strong>{info.label}</strong>
            {info.recorded_objects > 0 && <> ({info.recorded_objects} file(s) on record)</>}.
            {" "}<strong>Nothing is deleted from storage.</strong>
          </p>
          {info.location && (
            <p style={{ margin: "0 0 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                        wordBreak: "break-all", color: "#6b7280" }}>
              {info.location}
            </p>
          )}
          <p style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 8 }}>
            {look ? <Badge color={look.color}>{look.label}</Badge>
                  : <Badge color="amber">Nothing recorded</Badge>}
          </p>
          <p style={{ margin: "0 0 12px", color: "#6b7280" }}>{info.verified_detail}</p>
          {info.orphan_count > 0 && (
            <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(217,119,6,0.10)",
                          border: "1px solid rgba(217,119,6,0.45)", color: "#92400e", fontSize: 12 }}>
              <strong>
                {info.orphan_count}{info.orphans_truncated ? "+" : ""} object(s) will be left in
                storage with nothing pointing at them.
              </strong>{" "}
              Once this record is gone the entry written to the project log is the only pointer
              left. Clear them with an S3 client if they are not wanted.
              <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontFamily: "'JetBrains Mono', monospace",
                           fontSize: 11, wordBreak: "break-all" }}>
                {info.orphans.map(k => <li key={k}>{k}</li>)}
              </ul>
              {info.orphan_count > info.orphans.length && (
                <div style={{ marginTop: 6 }}>(and {info.orphan_count - info.orphans.length} more)</div>
              )}
            </div>
          )}
          {info.orphans_error && (
            <div style={{ fontSize: 12, color: "#92400e" }}>
              What is still in storage could not be listed ({info.orphans_error}), but nothing will
              be removed from it either way.
            </div>
          )}
        </div>
      )}
    </ConfirmDialog>
  );
}


// Worst first, so sorting a table brings the rows that need attention to the
// top. Shared: both the Submissions and the Results tab sort on it.
export const VERIFY_ORDER = ["missing", "partial", "unverifiable", null, "ok"];
